import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useActiveOrganization } from '@repo/api-client/react';
import {
  useCreateService,
  useDeleteService,
  useUpdateService,
} from '@repo/queries';
import type { Service } from '@repo/api-client/dtos';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { parseUserMoney } from '@/features/money';
import {
  serviceFormDefaults,
  serviceFormFromWire,
  serviceFormSchema,
  serviceFormToCreate,
  serviceFormToUpdate,
  type ServiceFormValues,
} from './serviceFormSchema';

const PRICING_UNITS = [
  { value: 'fixed', label: 'Fixed price' },
  { value: 'hour', label: 'Per hour' },
  { value: 'day', label: 'Per day' },
  { value: 'week', label: 'Per week' },
  { value: 'month', label: 'Per month' },
  { value: 'project', label: 'Per project' },
  { value: 'unit', label: 'Per unit' },
  { value: 'custom', label: 'Custom' },
];

interface ServiceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service?: Service | null;
}

export function ServiceFormDialog({
  open,
  onOpenChange,
  service,
}: ServiceFormDialogProps) {
  const org = useActiveOrganization();
  const create = useCreateService();
  const update = useUpdateService();
  const remove = useDeleteService();
  const { toast } = useToast();

  const form = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceFormSchema),
    defaultValues: service
      ? serviceFormFromWire(service)
      : serviceFormDefaults(org?.currency ?? 'USD'),
    values: service
      ? serviceFormFromWire(service)
      : serviceFormDefaults(org?.currency ?? 'USD'),
  });

  const editing = !!service;
  const pricingUnit = form.watch('pricingUnit');

  async function onSubmit(values: ServiceFormValues) {
    const parsed = parseUserMoney(values.pricingRateInput, values.pricingCurrency);
    if (!parsed.ok) {
      form.setError('pricingRateInput', { message: 'Enter a valid amount' });
      return;
    }
    const pricingRateMinor = parsed.value.amount.toString();
    try {
      if (editing && service) {
        await update.updateService({
          serviceId: service.id,
          updates: serviceFormToUpdate(values, pricingRateMinor),
        });
        toast({ title: 'Service saved', variant: 'success' });
      } else {
        await create.createService(serviceFormToCreate(values, pricingRateMinor));
        toast({ title: 'Service created', variant: 'success' });
      }
      onOpenChange(false);
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  async function onDelete() {
    if (!service) return;
    if (typeof window !== 'undefined' && !window.confirm(`Delete ${service.name}?`)) return;
    try {
      await remove.deleteService(service.id);
      toast({ title: 'Service deleted', variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }
  // Help eslint know update.error etc are surfaced via toast
  void update.error;
  void create.error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit service' : 'New service'}</DialogTitle>
          <DialogDescription>
            Reusable line items so you stop retyping prices when invoicing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Field
            label="Name"
            htmlFor="name"
            required
            error={form.formState.errors.name?.message}
          >
            <Input autoFocus {...form.register('name')} />
          </Field>
          <Field label="Description" htmlFor="description">
            <Textarea rows={2} {...form.register('description')} />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field
              label="Rate"
              htmlFor="pricingRateInput"
              required
              error={form.formState.errors.pricingRateInput?.message}
            >
              <Input
                inputMode="decimal"
                placeholder="0.00"
                {...form.register('pricingRateInput')}
              />
            </Field>
            <Field label="Currency" htmlFor="pricingCurrency">
              <Input maxLength={3} {...form.register('pricingCurrency')} />
            </Field>
            <Field label="Unit" htmlFor="pricingUnit">
              <Controller
                control={form.control}
                name="pricingUnit"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRICING_UNITS.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </div>
          {pricingUnit === 'custom' ? (
            <Field label="Custom unit label" htmlFor="pricingCustomUnit">
              <Input
                placeholder="e.g. per chapter"
                {...form.register('pricingCustomUnit')}
              />
            </Field>
          ) : null}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Tax rate (%)"
              htmlFor="taxRate"
              hint="Used when this service appears on a per-item-tax invoice."
              error={form.formState.errors.taxRate?.message}
            >
              <Input type="number" min={0} max={100} step="any" {...form.register('taxRate')} />
            </Field>
            <Controller
              control={form.control}
              name="taxable"
              render={({ field }) => (
                <SwitchField
                  label="Taxable"
                  description="Off if this service is exempt across all invoices."
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <Controller
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <SwitchField
                label="Active"
                description="Inactive services are hidden from the invoice composer."
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <DialogFooter className="justify-between">
            {editing ? (
              <Button
                type="button"
                variant="ghost"
                onClick={onDelete}
                disabled={remove.loading}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.loading || update.loading}
              >
                Cancel
              </Button>
              <Button type="submit" loading={create.loading || update.loading}>
                {editing ? 'Save service' : 'Create service'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SwitchField({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-dashed border-line bg-secondary/30 p-3">
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        {description ? (
          <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>
        ) : null}
      </div>
      <Switch checked={value} onCheckedChange={onChange} aria-label={label} />
    </label>
  );
}
